import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../context/AuthContext';

interface Category {
  id: number;
  name: string;
}

export default function AddListing() {
  const navigate = useNavigate();
  const { user, token } = useAuth(); // logged in user
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    providerName: '',
    specialty: '',
    providerType: '',
    yearsOfExperience: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    acceptingNewPatients: 'yes',
    languages: '',
    insuranceAccepted: '',
    services: '',
    conditions: '',
    officeHours: '',
    parkingAvailable: 'yes',
    wheelchairAccessible: 'yes',
    bio: '',
    medicalSchool: '',
    residency: '',
    boardCertifications: '',
    awards: '',
  });

  // Fetch selected package
  useEffect(() => {
    const packageData = sessionStorage.getItem('selectedPackage');
    if (packageData) {
      setSelectedPackage(JSON.parse(packageData));
    } else {
      navigate('/pricing');
    }
  }, [navigate]);

  // Fetch Directorist categories
  useEffect(() => {
    fetch('https://therevulist.com/wp-json/directorist/v1/listings/categories?hide_empty=false')
      .then(res => res.json())
      .then((data) => {
        setCategories(data.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
        })));
      })
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
  const { name, value } = e.target;

  setFormData({
    ...formData,
    // Convert providerType to number for API
    [name]: name === 'providerType' ? Number(value) : value,
  });
};


const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    if (!user || !token) throw new Error('You must be logged in to submit a listing.');
    if (!selectedCategory) throw new Error('Please select a category.');

    // Directorist v2 payload
    const payload = {
      title: formData.providerName,
      categories: [{ id: selectedCategory }], // Directorist expects this format
      ...formData,
      packageType: selectedPackage.packageType,
      author: user.id,
      // Required for v2
      terms_and_conditions: true,
      privacy_policy: true,
    };

    const response = await fetch(
      'https://therevulist.com/wp-json/directorist/v2/listings',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    const resData = await response.json();
    console.log('v2 response:', resData);

    if (!response.ok) {
      throw new Error(resData.message || 'Failed to submit listing.');
    }

    alert('Your listing has been submitted!');
    sessionStorage.removeItem('selectedPackage');
    sessionStorage.removeItem('listingData');
    navigate('/');
  } catch (err: any) {
    setError(err.message || 'Something went wrong. Please try again.');
  } finally {
    setLoading(false);
  }
};


  const nextStep = () => currentStep < 4 && setCurrentStep(currentStep + 1);
  const prevStep = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const steps = [
    { number: 1, title: 'Basic Info', icon: 'ri-user-line' },
    { number: 2, title: 'Location & Contact', icon: 'ri-map-pin-line' },
    { number: 3, title: 'Practice Details', icon: 'ri-hospital-line' },
    { number: 4, title: 'About & Credentials', icon: 'ri-file-text-line' },
  ];

  const getPackageName = (type: string) => {
    const names: Record<string, string> = {
      basic: 'Basic Listing',
      essential: 'Essential Listing',
      enhanced: 'Enhanced Listing',
      claim: 'Claim Your Listing',
    };
    return names[type] || type;
  };

  if (!selectedPackage) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Add Your Healthcare Listing
            </h1>
            <p className="text-xl text-gray-600">
              Join thousands of healthcare providers on Revulist
            </p>
          </div>

          {/* Selected Package */}
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-vip-crown-line text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-teal-600 font-medium">Selected Package</div>
                  <div className="text-xl font-bold text-gray-900">
                    {getPackageName(selectedPackage.packageType)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-teal-600">
                  {selectedPackage.price === 0 ? 'FREE' : `$${selectedPackage.price}`}
                </div>
                {selectedPackage.price > 0 && (
                  <div className="text-sm text-gray-600">/{selectedPackage.billingCycle}</div>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="mt-4 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i> Change Package
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        currentStep >= step.number ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      <i className={`${step.icon} text-xl`}></i>
                    </div>
                    <span
                      className={`mt-2 text-sm font-medium ${
                        currentStep >= step.number ? 'text-teal-600' : 'text-gray-500'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 flex-1 mx-4 transition-all duration-300 ${
                        currentStep > step.number ? 'bg-teal-600' : 'bg-gray-200'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && <p className="text-red-600 mb-4 text-center">{error}</p>}

          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">

              {/* STEP 1 */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label>Provider Name *</label>
                      <input
                        type="text"
                        name="providerName"
                        value={formData.providerName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label>Specialty *</label>
                      <input
                        type="text"
                        name="specialty"
                        value={formData.specialty}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label>Provider Type *</label>
                      <select
                        name="providerType"
                        value={formData.providerType}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setFormData({ ...formData, providerType: val });
                          setSelectedCategory(val);
                        }}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      >
                        <option value="">Select type</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Years of Experience *</label>
                      <input
                        type="number"
                        name="yearsOfExperience"
                        value={formData.yearsOfExperience}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                  </div>

                </div>
              )}

              {/* STEP 2 */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Location & Contact</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label>Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label>Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label>State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label>ZIP Code *</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Practice Details</h2>
                  <div>
                    <label>Accepting New Patients? *</label>
                    <div className="flex gap-4">
                      <label>
                        <input
                          type="radio"
                          name="acceptingNewPatients"
                          value="yes"
                          checked={formData.acceptingNewPatients === 'yes'}
                          onChange={handleChange}
                        />{' '}
                        Yes
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="acceptingNewPatients"
                          value="no"
                          checked={formData.acceptingNewPatients === 'no'}
                          onChange={handleChange}
                        />{' '}
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label>Languages Spoken *</label>
                    <input
                      type="text"
                      name="languages"
                      value={formData.languages}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Insurance Accepted *</label>
                    <textarea
                      name="insuranceAccepted"
                      value={formData.insuranceAccepted}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Services Offered *</label>
                    <textarea
                      name="services"
                      value={formData.services}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Conditions Treated</label>
                    <textarea
                      name="conditions"
                      value={formData.conditions}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Office Hours *</label>
                    <textarea
                      name="officeHours"
                      value={formData.officeHours}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label>Parking Available?</label>
                      <div className="flex gap-4">
                        <label>
                          <input
                            type="radio"
                            name="parkingAvailable"
                            value="yes"
                            checked={formData.parkingAvailable === 'yes'}
                            onChange={handleChange}
                          />{' '}
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="parkingAvailable"
                            value="no"
                            checked={formData.parkingAvailable === 'no'}
                            onChange={handleChange}
                          />{' '}
                          No
                        </label>
                      </div>
                    </div>
                    <div>
                      <label>Wheelchair Accessible?</label>
                      <div className="flex gap-4">
                        <label>
                          <input
                            type="radio"
                            name="wheelchairAccessible"
                            value="yes"
                            checked={formData.wheelchairAccessible === 'yes'}
                            onChange={handleChange}
                          />{' '}
                          Yes
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="wheelchairAccessible"
                            value="no"
                            checked={formData.wheelchairAccessible === 'no'}
                            onChange={handleChange}
                          />{' '}
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4 */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">About & Credentials</h2>
                  <div>
                    <label>Professional Bio *</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Medical School *</label>
                    <input
                      type="text"
                      name="medicalSchool"
                      value={formData.medicalSchool}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Residency *</label>
                    <input
                      type="text"
                      name="residency"
                      value={formData.residency}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Board Certifications</label>
                    <input
                      type="text"
                      name="boardCertifications"
                      value={formData.boardCertifications}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label>Awards & Recognition</label>
                    <input
                      type="text"
                      name="awards"
                      value={formData.awards}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-6 py-3 border rounded-lg hover:bg-gray-100"
                  >
                    Previous
                  </button>
                )}
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="ml-auto px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="ml-auto px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    {loading
                      ? 'Submitting...'
                      : selectedPackage.price === 0
                      ? 'Submit Listing'
                      : 'Proceed to Payment'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
